<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <v-toolbar flat color="white">
            <v-toolbar-title>Organizations</v-toolbar-title>
            <v-spacer/>
            <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    clearable
                    @click:clear="handleClearSearch"
                    v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <v-icon large class="mr-2" @click="loadItems">
                mdi-refresh
            </v-icon>
            <v-dialog v-model="dialog" scrollable max-width="540px">
                <template v-slot:activator="{ on }">
                    <v-btn color="accent" v-on="on">
                        <span style="color: #000000;">New Org</span>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">{{ formTitle }}</span>
                    </v-card-title>
                    <v-card-actions class="mr-2">
                            <v-spacer/>
                            <v-btn color="blue darken-1" text @click="closeDialog">Cancel</v-btn>
                            <v-btn color="blue darken-1" text :disabled="readonly" @click="save">Save</v-btn>
                    </v-card-actions>
                    <v-card-text id="orgDataId">
                        <v-progress-linear v-if="waiting" :indeterminate="true"/>
                        <OrgNameAddress :readonly="readonly" :orgData="orgData"/>
                        <OrgContactInfo :readonly="readonly" :orgData="orgData"/>
                        <OrgTelehealth :readonly="readonly" :orgData="orgData"/>
                        <v-container>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total PLR Tests Taken:</strong> {{ totalPlrTestsForOrg }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total NPC Tests Taken:</strong> {{ totalNpcTestsForOrg }}
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <v-dialog v-model="paymentPlanDialog" max-width="540px">
                <v-card>
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">Org Payment & Plan Info</span>
                    </v-card-title>
                    <v-card-text>
                        <OrgPaymentPlanInfoDialog :dialog="paymentPlanDialog" :editedIndex="editedIndex" :orgData="orgData"/>
                    </v-card-text>
                    <v-card-actions class="mr-2">
                        <v-spacer/>
                        <v-btn color="blue darken-1" text @click="closePaymentPlan">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <OrgHttpEndpointDialog :dialog="httpEndpointDialog" :editedIndex="editedIndex" @dialog-close="closeHttpEndpointDialog"/>
            <SendMessageDialog :dialog="sendMessageDialog" :orgName="orgData.name" :orgId="orgData.id" @dialog-close="closeSendMessageDialog"/>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        :headers="headers"
                        :items="orgs"
                        item-key="id"
                        @click:row="rowSelected"
                        @current-items="currentItems"
                        :single-expand=true
                        :expanded.sync="expanded"
                        :options.sync="pagination"
                        :server-items-length="totalRecords"
                        :loading="waiting"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.viewing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="viewItem(item)">
                                                mdi-compass
                                            </v-icon>
                                        </span>
                                </template>
                                <span>View</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.editing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="editItem(item)">
                                                mdi-pencil
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Edit</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.deleting>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="deleteItem(item)">
                                                mdi-delete
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                            <template v-if="!item.parentId">
                                <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.createChildOrg>
                                    <template #activator="{ on }">
                                            <span v-on="on">
                                                <v-icon class="mr-2" small @click="createChildOrg(item)">
                                                    mdi-account-child
                                                </v-icon>
                                            </span>
                                    </template>
                                    <span>Create Child Org</span>
                                </v-tooltip>
                            </template>
                            <template v-if="!item.parentId">
                                <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.paymentMethod>
                                    <template #activator="{ on }">
                                            <span v-on="on">
                                                <v-icon class="mr-2" small @click="paymentPlanItem(item)">
                                                    mdi-credit-card
                                                </v-icon>
                                            </span>
                                    </template>
                                    <span>Payment Method</span>
                                </v-tooltip>
                            </template>
                            <template v-if="!item.parentId">
                                <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.httpEndpoint>
                                    <template #activator="{ on }">
                                            <span v-on="on">
                                                <v-icon class="mr-2" small @click="httpEndpointItem(item)">
                                                    mdi-file-send
                                                </v-icon>
                                            </span>
                                    </template>
                                    <span>Http Endpoint Settings</span>
                                </v-tooltip>
                            </template>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.messaging>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="sendMessageItem(item)">
                                                mdi-message-text-outline
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Send Message</span>
                            </v-tooltip>
                        </template>
                        <template v-slot:item.expanded="{ item }">
                            <v-row justify-Left>
                                <v-icon large v-if="!expanded.includes(item)">mdi-chevron-right</v-icon>
                                <v-icon large v-else>mdi-chevron-down</v-icon>
                            </v-row>
                        </template>
                        <template v-slot:expanded-item="props">
                            <td :colspan=10>
                                <v-expand-transition>
                                    <v-card class="elevation-10">
                                        <v-card-text>
                                            <OrgListUsers :orgId="orgId"/>
                                        </v-card-text>
                                    </v-card>
                                </v-expand-transition>
                            </td>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmationDialog v-if="confirmDeleteOrg" :params="confirmDeleteOrgParams" @confirmation-dialog-response="confirmDeleteOrgHandler"></ConfirmationDialog>
    </div>
</template>

<script>
    const clonedeep = require('lodash.clonedeep');
    import OrgNameAddress from '@/components/org-name-address';
    import OrgContactInfo from '@/components/org-contact-info';
    import OrgTelehealth from '@/components/org-telehealth';
    import OrgPaymentPlanInfoDialog from '@/components/org-payment-plan-info-dialog';
    import OrgHttpEndpointDialog from '@/components/org-http-endpoint-dialog';
    import SendMessageDialog from '@/components/send-message-dialog';
    import OrgListUsers from '@/components/org-list-users';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import ConfirmationDialog from '@/components/confirmation-dialog';

    export default
    {
        name: 'orgs',
        components:
        {
            OrgNameAddress,
            OrgContactInfo,
            OrgTelehealth,
            OrgPaymentPlanInfoDialog,
            OrgHttpEndpointDialog,
            OrgListUsers,
            SendMessageDialog,
            ConfirmationDialog
        },
        data: function ()
        {
            return {
                tipDisabling:
                {
                    viewing: false,
                    editing: false,
                    deleting: false,
                    createChildOrg: false,
                    paymentMethod: false,
                    messaging: false,
                    httpEndpoint: false
                },
                newOrgItems:
                [
                    { title: 'Top Level Org' },
                    { title: 'Child Org' }
                ],
                orgId: '',
                expandOrHideRow: true,
                dialog: false,
                paymentPlanDialog: false,
                pagination: {},
                search: '',
                expanded: [],
                headers:
                [
                    {
                        text: 'Users',
                        value: 'expanded',
                        width: '40px',
                        sortable: false
                    },
                    {
                        text: 'Actions',
                        width: '200px',
                        value: 'actions',
                        sortable: false
                    },
                    {
                        text: 'Organization',
                        width: '300px',
                        value: 'name',
                        sortable: false
                    },
                    {
                        text: 'Type',
                        value: 'type',
                        sortable: false
                    },
                    {
                        text: 'Parent',
                        width: '300px',
                        value: 'parentName',
                        sortable: false
                    },
                    {
                        text: 'Org ID',
                        value: 'id',
                        sortable: false
                    },
                    {
                        text: 'Subscription ID',
                        value: 'paymentSubId',
                        sortable: false
                    },
                    {
                        text: 'Status',
                        value: 'status',
                        sortable: false
                    },
                    {
                        text: 'Customer ID',
                        value: 'paymentCusId',
                        sortable: false
                    },
                    {
                        text: 'Date Created',
                        width: '200px',
                        value: 'createdAt',
                        sortable: false
                    },
                    {
                        text: 'Subscription End Date',
                        width: '200px',
                        value: 'paymentSubEndDate',
                        sortable: false
                    }
                ],
                readonly: false,
                editedIndex: -1,
                orgData: {},
                defaultOrgData:
                {
                    name: '',
                    logo: '',
                    paymentSubEndDate: '',
                    paymentSubId: '',
                    address:
                    {
                        addrLn1: '',
                        addrLn2: '',
                        city: '',
                        state: '',
                        pstlCd: '',
                        cntry: ''
                    },
                    contactInfo:
                    {
                        name: '',
                        dprtmnt: '',
                        phone: '',
                        email: ''
                    },
                    stripeTelehealthSubscription:
                    {
                        subItemId: ''
                    },
                    telehealth:
                    {
                        emailSubject: '',
                        emailBody: ''
                    }
                },
                parentId: null,
                httpEndpointDialog: false,
                sendMessageDialog: false,
                confirmDeleteOrg: false,
                confirmDeleteOrgParams:
                {
                    title: 'Delete Organization',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                }
            }
        },
        created()
        {
            this.orgData = clonedeep(this.defaultOrgData)
        },
        destroyed()
        {
            this.CLEAR_USERS()
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser
            }),
            ...mapState('orgs',
            {
                orgs: state => state.orgs,
                totalRecords: state => state.totalRecords,
                totalPlrTestsForOrg: state => state.totalPlrTestsForOrg,
                totalNpcTestsForOrg: state => state.totalNpcTestsForOrg
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            formTitle()
            {
                if (this.readonly)
                {
                    return "View Org"
                }
                return this.editedIndex === -1 ? 'New Org' : 'Edit Org'
            }
        },
        watch:
        {
            search()
            {
                this.pagination.page = 1
            },
            dialog(val)
            {
                if (!val)
                {
                    document.getElementById('orgDataId').scrollTop = 0;
                    this.closeCommon()
                }
            },
            paymentPlanDialog(val)
            {
                if (!val)
                {
                    this.closeCommon()
                }
            }
        },
        methods:
        {
            ...mapActions('orgs', ['GET_ORGS', 'SET_ORG', 'DELETE_ORG', 'GET_TEST_COUNT_FOR_ORG']),
            ...mapMutations('orgs', ['SET_PLR_TEST_COUNT_FOR_ORG', 'SET_NPC_TEST_COUNT_FOR_ORG']),
            ...mapActions('customerBilling', ['GET_ORG_PAYMENT_INFO']),
            ...mapMutations('users', ['CLEAR_USERS']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            async handleSearch()
            {
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.search = '';
                await this.loadItems()
            },
            rowSelected(item)
            {
                // Do not expand or hide the row if an action in a row was selected
                if (!this.expandOrHideRow)
                {
                    this.expandOrHideRow = true
                }
                else
                {
                    this.expanded = this.expanded.length > 0 && this.expanded[0].id === item.id ? [] : [item]
                }
                this.orgId = item.id;
            },
            currentItems(items)
            {
                if (this.pagination.page !== 1 && 0 === items.length)
                {
                    this.pagination.page -= 1
                }
            },
            async loadItems()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_ORGS({pagination: this.pagination, search: this.search})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            async createChildOrg(item)
            {
                this.expandOrHideRow = false;
                this.parentId = item.id;
                this.dialog = true
            },
            async viewItem(item)
            {
                this.tipDisabling.viewing = true;
                this.readonly = true;
                this.commonViewEditItem(item);
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_TEST_COUNT_FOR_ORG({orgId: item.id})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            editItem(item)
            {
                this.tipDisabling.editing = true;
                this.readonly = false;
                this.SET_PLR_TEST_COUNT_FOR_ORG(0);
                this.SET_NPC_TEST_COUNT_FOR_ORG(0);
                this.commonViewEditItem(item)
            },
            async commonViewEditItem(item)
            {
                this.expandOrHideRow = false;
                this.editedIndex = this.orgs.indexOf(item);
                this.orgData = Object.assign({}, item);
                this.dialog = true
            },
            async deleteItem(item)
            {
                this.tipDisabling.deleting = true;
                this.expandOrHideRow = false;
                this.confirmDeleteOrgParams.text = `Are you sure you want to delete organization: ${item.name}?`;
                this.confirmDeleteOrgParams.context = item;
                this.confirmDeleteOrg = true
            },
            async confirmDeleteOrgHandler(payload)
            {
                this.confirmDeleteOrg = false;
                // If the organization should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        await this.DELETE_ORG({deleteIndex: this.orgs.indexOf(payload), orgId: payload.context.id});
                        await this.loadItems()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            closeDialog()
            {
                this.dialog = false
            },
            closeCommon()
            {
                this.WAITING(false);
                this.readonly = true;
                setTimeout(() =>
                {
                    this.orgData = clonedeep(this.defaultOrgData);
                    this.readonly = false;
                    this.editedIndex = -1;
                    this.parentId = null
                }, 300)
            },
            async save()
            {
                this.HIDE_SNACKBAR();
                this.WAITING(true);
                // If the org is being edited
                if (this.editedIndex >= 0)
                {
                    try
                    {
                        await this.SET_ORG({editedIndex: this.editedIndex, data: this.orgData})
                    }
                    catch (error)
                    {
                        this.WAITING(false);
                        this.readonly = false;
                        this.SHOW_SNACKBAR_ERROR(error.message);
                        return
                    }
                }
                else // Else this is a new org
                {
                    try
                    {
                        // If a child org is being created explicitly
                        if (this.parentId)
                        {
                            this.orgData.parentId = this.parentId
                        }
                        // If the user is an org admin, they can only create a child org
                        else if (this.currentUser && this.currentUser.authorities && this.currentUser.authorities[0].role === 'ROLE_ORG_ADMIN')
                        {
                            this.orgData.parentId = this.currentUser.orgId
                        }
                        await this.SET_ORG({data: this.orgData})
                    }
                    catch (error)
                    {
                        this.WAITING(false);
                        this.readonly = false;
                        this.SHOW_SNACKBAR_ERROR(error.message);
                        return
                    }
                }
                this.dialog = false;
                this.loadItems()
            },
            async paymentPlanItem(item)
            {
                this.tipDisabling.paymentPlan = true;
                this.expandOrHideRow = false;
                this.editedIndex = this.orgs.indexOf(item);
                this.orgData = item;
                this.WAITING(true);
                try
                {
                    await this.GET_ORG_PAYMENT_INFO(this.orgData['id'])
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false);
                this.paymentPlanDialog = true
            },
            closePaymentPlan()
            {
                this.paymentPlanDialog = false;
                setTimeout(() =>
                {
                    this.closeCommon()
                }, 300)
            },
            async httpEndpointItem(item)
            {
                this.tipDisabling.httpEndpoint = true;
                this.expandOrHideRow = false;
                this.editedIndex = this.orgs.indexOf(item);
                this.httpEndpointDialog = true
            },
            closeHttpEndpointDialog()
            {
                this.httpEndpointDialog = false
            },
            sendMessageItem(item)
            {
                this.tipDisabling.messaging = true;
                this.expandOrHideRow = false;
                this.orgData = Object.assign({}, item);
                this.sendMessageDialog = true
            },
            closeSendMessageDialog()
            {
                this.sendMessageDialog = false
            }
        }
    }

</script>

<style scoped>
</style>